import React from 'react'
import axios, { AxiosError } from 'axios'
import { storageAuthTokenGet, storageAuthTokenSave } from '../storage/storageAuthToken'

type PromiseType = {
  onSuccess: (token: string) => void
  onFailure: (error: AxiosError) => void
}

let failedQueue: PromiseType[] = []
let isRefreshing = false

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST
})

api.interceptors.request.use(async config => {
  const auth = storageAuthTokenGet()
  config.headers = {
    'Content-Type': 'application/json'
  }
  if (config.url === '/professionals/profileImage') {
    config.headers = {
      'Content-Type': 'multipart/form-data'
    }
  }
  if (auth?.accessToken && config.url !== '/auth/refreshToken') {
    config.headers.Authorization = `Bearer ${auth.accessToken}`
  }
  return config
})

function signOut () {
  localStorage.clear()
  location.href = '/'
}

api.interceptors.response.use(
  response => response,
  async requestError => {
    if (requestError?.response?.status === 401) {
      const { refreshToken } = storageAuthTokenGet()

      if (!refreshToken) {
        signOut()
        return await Promise.reject(requestError)
      }

      const originalRequestConfig = requestError.config

      if (isRefreshing) {
        return await new Promise((resolve, reject) => {
          failedQueue.push({
            onSuccess: (token: string) => {
              originalRequestConfig.headers = {
                Authorization: `Bearer ${token}`
              }
              resolve(api(originalRequestConfig))
            },
            onFailure: (error: AxiosError) => {
              reject(error)
            }
          })
        })
      }

      isRefreshing = true

      // eslint-disable-next-line no-async-promise-executor
      return await new Promise(async (resolve, reject) => {
        try {
          const { data } = await api.post('/auth/refreshToken', {
            refreshToken
          })
          await storageAuthTokenSave({
            accessToken: data.access_token,
            refreshToken: data.refresh_token
          })

          if (originalRequestConfig.data) {
            originalRequestConfig.data = JSON.parse(
              originalRequestConfig.data
            )
          }

          originalRequestConfig.headers = {
            Authorization: `Bearer ${data.access_token}`
          }

          failedQueue.forEach(request => {
            request.onSuccess(data.access_token)
          })

          resolve(api(originalRequestConfig))
        } catch (error: any) {
          failedQueue.forEach(request => {
            request.onFailure(error)
          })
          signOut()
          reject(error)
        } finally {
          isRefreshing = false
          failedQueue = []
        }
      })
    }

    return await Promise.reject(requestError)
  }
)

export default api
